/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Facebook Pixel Lead tracking for recruiting buttons on karriere page
 * Tracks "Lead" event when users click buttons linking to /pages/jobs
 * Uses event delegation to catch dynamically loaded buttons
 */
(function() {
  const isCareersPage = window.location.pathname === '/pages/karriere' || 
                         window.location.pathname.includes('/pages/karriere');

  if (!isCareersPage) {
    return;
  }

  function trackFacebookLead(link) {
    const buttonLocation = link.closest('.hero-section, .hero-banner, .hero-content') 
      ? 'hero' 
      : link.closest('.careers-highlight-section') 
        ? 'careers_highlight' 
        : 'unknown';
    
    const eventData = {
      content_name: 'Recruiting Button Click',
      content_category: 'Careers',
      source: 'karriere_page',
      button_location: buttonLocation,
      page_url: window.location.href
    };

    if (typeof fbq !== 'undefined') {
      fbq('track', 'Lead', eventData);
    } else {
      window._fbq = window._fbq || [];
      window._fbq.push(['track', 'Lead', eventData]);
    }
  }

  function handleClick(event) {
    const link = event.target.closest('a');
    
    if (link) {
      const hrefAttr = link.getAttribute('href');
      const hrefResolved = link.href;
      
      if ((hrefAttr && hrefAttr.includes('/pages/jobs')) ||
          (hrefResolved && hrefResolved.includes('/pages/jobs'))) {
        trackFacebookLead(link);
      }
    }
  }

  document.addEventListener('click', handleClick, true);
})();



/**
 * Custom variant UI – central place for UI updates on variant change.
 * Add new update logic in _update* methods and call them from update(variant).
 * Variant metafields are NOT in the product JSON; use Liquid data attributes (e.g. data-variant-ids-with-warning).
 */
class CustomVariantUI {
  constructor(form) {
    this.form = form;
  }

  update(variant) {
    if (!this.form) return;
    this._updateSizeNote(variant);
    // Add more UI updates here, e.g. this._updateCustomBadge(variant);
  }

  _updateSizeNote(variant) {
    const container = this.form.closest('.product-form');
    if (!container) return;

    const noteEls = container.querySelectorAll('.product-form__option-size-info[data-size-note="true"]');
    if (!noteEls.length) return;

    if (!variant) {
      noteEls.forEach((noteEl) => {
        noteEl.style.display = 'none';
      });
      return;
    }

    noteEls.forEach((noteEl) => {
      const idsStr = noteEl.getAttribute('data-variant-ids-with-warning') || '';
      const variantIdsWithWarning = idsStr
        ? idsStr.split(',').map((id) => id.trim()).filter(Boolean)
        : [];

      const showWarning = variantIdsWithWarning.indexOf(String(variant.id)) !== -1;

      noteEl.style.display = showWarning ? 'block' : 'none';
    });
  }
}

function getCustomVariantUI(form) {
  if (!form._customVariantUI) {
    form._customVariantUI = new CustomVariantUI(form);
  }
  return form._customVariantUI;
}

document.addEventListener('variant:changed', (event) => {
  if (!event?.target) return;
  const form = event.target.closest?.('form');
  if (!form) return;

  const variant = event.detail?.variant;
  getCustomVariantUI(form).update(variant);
});

const SCROLL_INTO_VIEW_COMBO_BOX_DESKTOP_MQ = window.matchMedia('(min-width: 1000px)');
const SCROLL_INTO_VIEW_COMBO_BOX_VIEWPORT_PADDING = 16;
const SCROLL_INTO_VIEW_COMBO_BOX_SCROLL_THRESHOLD = 4;

function getScrollIntoViewComboBoxStickyHeaderOffset() {
  const documentStyles = getComputedStyle(document.documentElement);
  return (
    parseInt(documentStyles.getPropertyValue('--header-height') || 0, 10) *
      parseInt(documentStyles.getPropertyValue('--enable-sticky-header') || 0, 10) +
    parseInt(documentStyles.getPropertyValue('--announcement-bar-height') || 0, 10) *
      parseInt(documentStyles.getPropertyValue('--enable-sticky-announcement-bar') || 0, 10)
  );
}

function refreshScrollIntoViewComboBoxTopClass(comboBox) {
  const rect = comboBox.getBoundingClientRect();
  comboBox.classList.toggle(
    'combo-box--top',
    rect.top >= (window.innerHeight / 2) * 1.5
  );
}

function ensureScrollIntoViewComboBoxVisible(comboBox) {
  if (!comboBox?.open) return;

  const rect = comboBox.getBoundingClientRect();
  const viewportTop = getScrollIntoViewComboBoxStickyHeaderOffset() + SCROLL_INTO_VIEW_COMBO_BOX_VIEWPORT_PADDING;
  const viewportBottom = window.innerHeight - SCROLL_INTO_VIEW_COMBO_BOX_VIEWPORT_PADDING;
  const safeHeight = viewportBottom - viewportTop;

  if (rect.top >= viewportTop && rect.bottom <= viewportBottom) {
    return;
  }

  let scrollDelta = 0;

  if (rect.height > safeHeight) {
    scrollDelta = rect.top - viewportTop;
  } else if (rect.top < viewportTop) {
    scrollDelta = rect.top - viewportTop;
  } else if (rect.bottom > viewportBottom) {
    scrollDelta = rect.bottom - viewportBottom;
  }

  if (Math.abs(scrollDelta) <= SCROLL_INTO_VIEW_COMBO_BOX_SCROLL_THRESHOLD) {
    return;
  }

  const scrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : 'smooth';

  window.scrollBy({ top: scrollDelta, behavior: scrollBehavior });

  const onScrollSettled = () => {
    refreshScrollIntoViewComboBoxTopClass(comboBox);
  };

  if ('onscrollend' in window) {
    window.addEventListener('scrollend', onScrollSettled, { once: true });
  } else {
    setTimeout(onScrollSettled, scrollBehavior === 'smooth' ? 400 : 0);
  }
}

document.addEventListener('openable-element:open', (event) => {
  const comboBox = event.target;
  if (!comboBox?.hasAttribute?.('data-scroll-into-view-on-open')) return;
  if (!SCROLL_INTO_VIEW_COMBO_BOX_DESKTOP_MQ.matches) return;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      ensureScrollIntoViewComboBoxVisible(comboBox);
    });
  });
});
